<template>
  <v-container>
    <div class="ml-2">
      <PageTitle
        class="ft font-weight-bold font-size-md"
        text="Backlog Fuel Stock"
      />
    </div>

    <v-card class="elevation-1 pt-5 pb-4 mt-3" style="border-radius: 8px">
      <ValidationObserver v-slot="{ handleSubmit }">
        <v-form @submit.prevent="handleSubmit(saveBacklogStock)" class="px-3">
          <div class="d-flex flex-row flex-grow-1">
            <ValidationProvider
              rules="required"
              class="d-flex flex-grow-1 mx-2"
              v-slot="{ errors }"
            >
              <v-autocomplete
                :items="branches"
                outlined
                :loading="listLoading"
                class="ft font-weight-medium font-size-md"
                v-model="branchId"
                label="Choose A Branch"
                item-text="name"
                item-value="id"
                :error-messages="errors[0]"
              />
            </ValidationProvider>

            <ValidationProvider
              rules="required"
              class="d-flex flex-grow-1 mx-2"
              v-slot="{ errors }"
            >
              <v-menu
                v-model="menu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="date"
                    v-bind="attrs"
                    v-on="on"
                    outlined
                    readonly
                    prepend-inner-icon="event"
                    label="Backlog Stock Date"
                    :error-messages="errors[0]"
                    class="ft font-weight-medium font-size-md"
                  />
                </template>
                <v-date-picker
                  v-model="date"
                  :max="today"
                  @input="menu = false"
                />
              </v-menu>
            </ValidationProvider>
          </div>

          <v-alert
            dense
            outlined
            type="info"
            class="ft font-weight-medium mx-2"
          >
            Backlog fuel stock is saved separately from current stock and is
            consumed only by backlog fuel sales.
          </v-alert>
          <v-alert
            v-if="prefillLoading"
            dense
            outlined
            type="info"
            class="ft font-weight-medium mx-2"
          >
            Checking for existing backlog stock records...
          </v-alert>
          <v-alert
            v-else-if="hasExistingBacklogStock"
            dense
            outlined
            type="warning"
            class="ft font-weight-medium mx-2"
          >
            Existing backlog stock records were loaded for this branch and
            month. Saving will update those historical values.
          </v-alert>

          <div
            class="d-flex flex-column flex-fill pa-4 ma-1"
            style="background-color: #eeeeee; border-radius: 6px"
          >
            <SkeletonPreloader
              v-if="listLoading"
              class="d-flex flex-column my-1"
              :count="6"
              type="list-item-two-line"
            />
            <template v-else>
              <v-card
                v-for="row in stockLineItems"
                :key="row.productId"
                class="elevation-0 d-flex flex-column flex-fill mt-2 pt-6 mb-2 px-2"
              >
                <div class="d-flex flex-row flex-grow-1">
                  <v-text-field
                    v-model="row.name"
                    readonly
                    outlined
                    prepend-inner-icon="category"
                    label="Product"
                    class="ft font-weight-medium mx-2"
                  />
                  <ValidationProvider
                    rules="required|decimals"
                    class="d-flex flex-grow-1 mx-2"
                    v-slot="{ errors }"
                  >
                    <v-text-field
                      outlined
                      v-model="row.openingStock"
                      prepend-inner-icon="all_inclusive"
                      label="Historical Stock Level"
                      suffix="Lts"
                      :error-messages="errors[0]"
                      class="ft font-weight-medium"
                    />
                  </ValidationProvider>
                  <ValidationProvider
                    rules="decimals"
                    class="d-flex flex-grow-1 mx-2"
                    v-slot="{ errors }"
                  >
                    <v-text-field
                      outlined
                      v-model="row.meterReading"
                      prepend-inner-icon="speed"
                      label="Reference Meter Reading"
                      :error-messages="errors[0]"
                      class="ft font-weight-medium"
                    />
                  </ValidationProvider>
                </div>
              </v-card>
            </template>
          </div>

          <div class="d-flex flex-row flex-grow-1 justify-end mt-4 mr-3">
            <v-btn
              :disabled="isLoading || !branchId || stockLineItems.length === 0"
              :loading="isLoading"
              color="primary"
              type="submit"
            >
              Save
            </v-btn>
          </div>
        </v-form>
      </ValidationObserver>
    </v-card>

    <SnackBar
      :showSnackBar="showSnackBar"
      :message="message"
      :status="status"
    />
  </v-container>
</template>

<script>
import moment from "moment";
import { mapGetters } from "vuex";
import { required } from "vee-validate/dist/rules";
import { ValidationObserver, ValidationProvider, extend } from "vee-validate";
import { Api } from "../../../../api/Api";
import PageTitle from "../../../../components/PageTitle";
import { decimals } from "../../../utils/resolvers";

extend("required", {
  ...required,
  message: "Field is required"
});
extend("decimals", decimals);

export default {
  name: "AddBacklogProductsStock",
  components: {
    PageTitle,
    ValidationProvider,
    ValidationObserver,
    SnackBar: () => import(`../../../../components/SnackBar.vue`),
    SkeletonPreloader: () => import(`../../../../components/SkeletonPreloader`)
  },
  data() {
    return {
      branchId: "",
      menu: false,
      today: moment().format("YYYY-MM-DD"),
      date: moment()
        .subtract(1, "day")
        .format("YYYY-MM-DD"),
      existingBacklogStocks: [],
      prefillLoading: false,
      prefillRequestKey: "",
      stockLineItems: []
    };
  },
  computed: {
    ...mapGetters({
      showSnackBar: "getSnackBarState",
      message: "getSnackBarMessage",
      status: "getSnackBarStatus",
      products: "products/getProductList",
      branches: "branches/getAllBranches",
      listLoading: "getIsListLoading",
      isLoading: "getIsLoading"
    }),
    hasExistingBacklogStock() {
      return this.existingBacklogStocks.length > 0;
    }
  },
  watch: {
    branchId() {
      this.fetchExistingBacklogStock();
    },
    date() {
      this.fetchExistingBacklogStock();
    },
    products: {
      immediate: true,
      handler(products) {
        this.buildStockLineItems(products, this.existingBacklogStocks);
        this.fetchExistingBacklogStock();
      }
    }
  },
  methods: {
    getStockProductId(stock) {
      return stock?.productId?.id || stock?.productId;
    },
    buildStockLineItems(products = this.products, existingStocks = []) {
      const existingStocksByProduct = existingStocks.reduce((carry, stock) => {
        carry[this.getStockProductId(stock)] = stock;
        return carry;
      }, {});

      this.stockLineItems = products
        .filter(product => !product.isLube)
        .map(product => {
          const existingStock = existingStocksByProduct[product.id];
          return {
            productId: product.id,
            name: product.name,
            openingStock: existingStock
              ? Number(existingStock.quantity ?? 0)
              : 0,
            minimumThreshold: existingStock
              ? Number(existingStock.minimumThreshold ?? 0)
              : product.minimumThreshold ?? 0,
            meterReading: existingStock
              ? Number(existingStock.meterReading ?? 0)
              : 0
          };
        });
    },
    async fetchExistingBacklogStock() {
      if (!this.branchId || !this.date) {
        this.existingBacklogStocks = [];
        this.buildStockLineItems(this.products, []);
        return;
      }

      const requestKey = `${this.branchId}-${this.date}`;
      this.prefillRequestKey = requestKey;
      this.prefillLoading = true;

      try {
        const response = await Api().get(
          `/stocks/backlog/openings/${this.branchId}/products?date=${this.date}`
        );

        if (this.prefillRequestKey !== requestKey) {
          return;
        }

        this.existingBacklogStocks = Array.isArray(response.data)
          ? response.data
          : [];
        this.buildStockLineItems(this.products, this.existingBacklogStocks);
      } catch (e) {
        const errorMessage = e?.response?.data?.message;
        this.$store.dispatch("showSnackBar", {
          snackbar: true,
          message: Array.isArray(errorMessage)
            ? errorMessage.join(", ")
            : errorMessage ?? "Unable to fetch existing backlog fuel stock",
          status: "red"
        });
      } finally {
        if (this.prefillRequestKey === requestKey) {
          this.prefillLoading = false;
        }
      }
    },
    async saveBacklogStock() {
      try {
        this.$store.dispatch("isLoading", true);
        await Api().post(`/stocks/backlog/openings/${this.branchId}/products`, {
          date: this.date,
          stockLineItems: this.stockLineItems.map(
            ({ productId, openingStock, minimumThreshold, meterReading }) => ({
              productId,
              openingStock: Number(openingStock),
              minimumThreshold: Number(minimumThreshold ?? 0),
              meterReading: Number(meterReading ?? 0)
            })
          )
        });
        this.$store.dispatch("isLoading", false);
        this.$store.dispatch("showSnackBar", {
          snackbar: true,
          message: "Backlog fuel stock saved successfully",
          status: "success"
        });
        await this.fetchExistingBacklogStock();
      } catch (e) {
        const errorMessage = e?.response?.data?.message;
        this.$store.dispatch("isLoading", false);
        this.$store.dispatch("showSnackBar", {
          snackbar: true,
          message: Array.isArray(errorMessage)
            ? errorMessage.join(", ")
            : errorMessage ?? "Unable to save backlog fuel stock",
          status: "red"
        });
      }
    }
  },
  created() {
    this.$store.dispatch("branches/list");
    this.$store.dispatch("products/list", {
      isPaginated: false,
      page: 1,
      itemsPerPage: 100
    });
  }
};
</script>
